import {
  Box,
  Stack,
  Chip,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  MenuList,
} from '@mui/material';
import React, { useState } from 'react';
import ClearIcon from '@mui/icons-material/Clear';
import SwapVertRoundedIcon from '@mui/icons-material/SwapVertRounded';
import VerticalAlignTopRoundedIcon from '@mui/icons-material/VerticalAlignTopRounded';
import VerticalAlignBottomRoundedIcon from '@mui/icons-material/VerticalAlignBottomRounded';

import type { SortOrderDirection } from './HistoryDialog.types.ts';
import { useHistoryDialogFilters } from './useHistoryDialogFilters.ts';

type SortMenuProps = {
  sortOrder: SortOrderDirection;
  setSortOrder: (sortOrder: SortOrderDirection) => void;
};

const SortMenu = ({ sortOrder, setSortOrder }: SortMenuProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleOnClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const onSelect = (sortOrder: SortOrderDirection) => () => {
    setSortOrder(sortOrder);
    setAnchorEl(null);
  };

  const handleOnClose = () => {
    setAnchorEl(null);
  };

  const hasActiveSort = sortOrder !== 'desc';

  return (
    <div>
      <IconButton
        id="sort-menu-button"
        aria-controls={open ? 'sort-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleOnClick}
        size="small"
        color={hasActiveSort ? 'primary' : 'default'}
      >
        <SwapVertRoundedIcon />
      </IconButton>
      <Menu id="sort-menu" anchorEl={anchorEl} open={open} onClose={handleOnClose}>
        <MenuList>
          <MenuItem onClick={onSelect('desc')} selected={sortOrder === 'desc'}>
            <ListItemIcon>
              <VerticalAlignTopRoundedIcon />
            </ListItemIcon>
            <ListItemText>Newest First</ListItemText>
          </MenuItem>
          <MenuItem onClick={onSelect('asc')} selected={sortOrder === 'asc'}>
            <ListItemIcon>
              <VerticalAlignBottomRoundedIcon />
            </ListItemIcon>
            <ListItemText>Oldest First</ListItemText>
          </MenuItem>
        </MenuList>
      </Menu>
    </div>
  );
};

export const FilterControls: React.FC = () => {
  const { filters, setFilters } = useHistoryDialogFilters();
  const { sortOrder, activeStep } = filters;

  const handleOnStepDelete = () => {
    setFilters({ activeStep: null });
  };

  const handleOnClearFilters = () => {
    setFilters(null);
  };

  const setSortOrder = (sortOrder: SortOrderDirection) => {
    setFilters({ sortOrder });
  };

  const hasActiveFilters = !!activeStep || sortOrder !== 'desc';

  return (
    <Box>
      <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" sx={{ minHeight: 32 }}>
        <SortMenu sortOrder={sortOrder} setSortOrder={setSortOrder} />

        {!!activeStep && (
          <Chip
            label={activeStep.name.replace(/_/g, ' ')}
            onDelete={handleOnStepDelete}
            color="primary"
            variant="outlined"
            size="small"
            sx={{
              fontSize: '0.75rem',
            }}
          />
        )}

        {hasActiveFilters && (
          <>
            <Divider
              orientation="vertical"
              flexItem
              sx={{
                height: '1rem',
                my: 'auto',
              }}
            />

            <Tooltip title="Clear all filters" arrow>
              <IconButton size="small" onClick={handleOnClearFilters} aria-label="Clear all filters">
                <ClearIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Stack>
    </Box>
  );
};
